import { useState } from 'react';
import {
  downloadExcel,
  getSortColumn,
  jsonToExcel,
  setSortColumn,
} from '../../../../store/utils';
import { Control, ControlContainer } from '../../../Controls/styled';
import { ButtonHeader } from '../../../Header/Style';
import SVG from '../../../SVG';
import Table from '../../../Table';
import useValidityController from '../useValidityController';

const useValiditiesController = () => {
  const {
    getModal,
    setModal,
    currentRoute,
    enqueueSnackbar,
    Can,
    filterProducts,
    getFormMessage,
    validateForm,
    getValidity,
    setValidity,
    autocompleteProducts,
    ability,
    filterBranches,
    createValidity,
    updateValidity,
    filterValidities,
    deleteValidities: deleteValiditiesAction,
    autocompleteValidities,
    filterCompleteValidities,
    filterValidityStatusValidity,
  } = useValidityController();

  const branches = getValidity('filters.branches.data');

  const searchFilter = getValidity('filters.validities.filter');
  const setSearchFilter = (filter: SearchFilter) =>
    setValidity('filters.validities.filter', filter);

  const validities = getValidity('filters.validities.data.data');
  const editValidity = ({
    productId,
    subProductId,
    productFullDescription,
    productShortDescription,
    barcodeNumber,
    sectionId,
    sectionDescription,
    branchCode,
    branchName,
    validity,
    exitPackaging,
    quantity,
    statusName,
    statusColor,
    observation,
    id,
  }: Validity) => {
    setModal('single.updateValidity', true);
    setValidity('forms.validity.update', {
      productId,
      subProductId,
      productFullDescription,
      productShortDescription,
      barcodeNumber,
      sectionId,
      sectionDescription,
      branchCode,
      branchName,
      validity,
      exitPackaging,
      quantity,
      statusName,
      statusColor,
      observation,
      id,
    });
  };

  const checkValidityIds = getValidity('selecteds.validities');
  const hasCheckedValidities = checkValidityIds.length > 0;
  const hasFilters = (searchFilter.filter?.length ?? 0) > 0;
  const clearAll = () => {
    setValidity('filters.validities.filter', {
      ...getValidity('filters.validities.filter'),
      filter: [],
      orderBy: [],
      page: 1,
    });
    setValidity('selecteds.validities', []);
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownload = () => {
    filterCompleteValidities()
      .then(({ data: validities }) => {
        const data = [
          ...validities.map(
            ({
              productId,
              subProductId,
              productFullDescription,
              productShortDescription,
              barcodeNumber,
              sectionId,
              sectionDescription,
              branchCode,
              branchName,
              validity,
              exitPackaging,
              quantity,
              statusName,
              observation,
              createdAt,
            }) => ({
              'Id produto': productId,
              'Id subproduto': subProductId,
              'Descrição completa do produto': productFullDescription,
              'Descrição curta do produto': productShortDescription,
              'Código de barras': barcodeNumber,
              'Id seção': sectionId,
              'Descrição da seção': sectionDescription,
              'Código da filial': branchCode,
              'Nome da filial': branchName,
              Data: new Date(createdAt).toLocaleString('pt-BR').split(',')[0],
              Validade: new Date(validity)
                .toLocaleString('pt-BR')
                .split(',')[0],
              'Embalagem de saída': exitPackaging,
              Quantidade: quantity,
              Status: statusName,
              Observação: observation,
            }),
          ),
        ];
        const sheetname = 'Validades';
        const workbook = jsonToExcel(data, sheetname);
        const filename = `validades_${new Date().toLocaleString('pt-BR').replaceAll('/', '-').replaceAll(':', '-').replaceAll(', ', '_')}.xlsx`;
        downloadExcel(workbook, filename);
        enqueueSnackbar('Validades baixadas com sucesso', {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao baixar as validades', {
          variant: 'error',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const ValidityControls = () => {
    return currentRoute.includes('/tools/validity/validities') ? (
      <>
        <Can I="create" a="Validity">
          {() => (
            <ButtonHeader
              className="button-header"
              onClick={() => {
                setModal('single.createValidity', true);
              }}
            >
              <div
                style={{
                  marginRight: '20px',
                }}
              >
                <SVG.Icon.Plus fill="#364856" />
              </div>
              NOVA VALIDADE
            </ButtonHeader>
          )}
        </Can>
        <ControlContainer>
          {!downloadLoading ? (
            <Control onClick={handleDownload}>
              <SVG.Icon.Download color="#364856" width={24} height={24} />
            </Control>
          ) : (
            <Control>
              <SVG.Icon.Loader width={24} height={24} />
            </Control>
          )}

          <Can I="delete" a="Validity">
            {() => (
              <Control
                active={hasCheckedValidities}
                disabled={!hasCheckedValidities}
                onClick={() => {
                  setModal('single.deleteValidity', true);
                }}
              >
                <SVG.Icon.Trash
                  color={hasCheckedValidities ? '#364856' : '#a7a7a7'}
                  width={24}
                  height={24}
                />
              </Control>
            )}
          </Can>
          <Control onClick={clearAll}>
            <SVG.Icon.SVGIconGrayBroom color="#364856" width={24} height={24} />
          </Control>
          <Control
            onClick={() => {
              setModal('single.filterValidity', true);
            }}
          >
            <SVG.Icon.Filter width={24} height={24} borderOnly={!hasFilters} />
          </Control>
        </ControlContainer>
      </>
    ) : null;
  };

  const setCheckValidityIds = (selectedValidityIds: string[]) =>
    setValidity('selecteds.validities', selectedValidityIds);

  const checkValidityId = (validityId: string) => {
    const newcheckIds = checkValidityIds.includes(validityId)
      ? checkValidityIds.filter((id) => id !== validityId)
      : [...checkValidityIds, validityId];
    setCheckValidityIds(newcheckIds);
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const validityId = validities[index].id;
    checkValidityId(validityId);
  };

  const allValidityChecks = validities.every(({ id }) =>
    checkValidityIds.includes(id),
  );

  const validityCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckValidityIds(validities.map(({ id }) => id));
    } else {
      setCheckValidityIds([]);
    }
  };

  const total = getValidity('filters.validities.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });
  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });
  const TableControl = () => {
    return (
      <Table.Control
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalOfItems={total}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
      />
    );
  };

  const deleteValidityLoadings = getValidity('loadings.deleteValidities');

  const setDeleteValidityLoadings = (setDeleteValidityLoadings: boolean[]) =>
    setValidity('loadings.deleteValidities', setDeleteValidityLoadings);

  const deleteValidities = (validityIds: string[] = checkValidityIds) =>
    new Promise((resolve, reject) => {
      {
        const length = validityIds.length;

        const indexes = validityIds.map((invoiceId) =>
          validities.findIndex(({ id }) => invoiceId === id),
        );
        setDeleteValidityLoadings(
          validities.map((_, i) =>
            indexes.includes(i) ? true : deleteValidityLoadings[i],
          ),
        );
        deleteValiditiesAction(validityIds)
          .then(() => {
            const newCheckIds = checkValidityIds.filter(
              (id) => !validityIds.includes(id),
            );
            setCheckValidityIds(newCheckIds);
            setValidity('filters.validities.data.data', [
              ...validities.filter(({ id }) => !validityIds.includes(id)),
            ]);
            setTimeout(() => {
              filterValidities();
            }, 1000);

            resolve(true);
            enqueueSnackbar(
              length === 1
                ? 'Validade excluída com sucesso'
                : 'Validades foram excluídas com sucesso',
              {
                variant: 'success',
              },
            );
          })
          .catch(() => {
            reject(false);
            enqueueSnackbar(
              length === 1
                ? 'Houve uma falha ao excluir a validade'
                : 'Houve uma falha ao excluir as validades',
              {
                variant: 'error',
              },
            );
          })
          .finally(() => {
            setDeleteValidityLoadings(
              validities.map((_, i) =>
                indexes.includes(i) ? false : deleteValidityLoadings[i],
              ),
            );
          });
      }
    });
  const [loadingValidities, setLoadingValidities] = useState(false);
  const priorityItems = [
    {
      id: 0,
      name: 'Alta',
      data: 'high',
    },
    {
      id: 1,
      name: 'Média',
      data: 'medium',
    },
    {
      id: 2,
      name: 'Baixa',
      data: 'low',
    },
    {
      id: 3,
      name: 'Vencido',
      data: 'defeated',
    },
  ];
  return {
    loadingValidities,
    setLoadingValidities,
    getModal,
    setModal,
    ValidityControls,
    filterProducts,
    getFormMessage,
    validateForm,
    getValidity,
    setValidity,
    enqueueSnackbar,
    autocompleteProducts,
    searchFilter,
    setSearchFilter,
    validities,
    Can,
    ability,
    editValidity,
    branches,
    filterBranches,
    createValidity,
    updateValidity,
    filterValidities,
    onLineClickHandler,
    allValidityChecks,
    validityCheckAll,
    checkValidityIds,
    checkValidityId,
    TableControl,
    deleteValidityLoadings,
    deleteValidities,
    autocompleteValidities,
    getSortField,
    setSortField,
    filterValidityStatusValidity,
    priorityItems
  };
};

export default useValiditiesController;
